/**
 * Umami Analytics Utility
 * Safe wrapper for Umami tracking with TypeScript support
 */

// Extend window interface to include umami and gtag
declare global {
  interface Window {
    umami?: {
      track: (eventName: string, eventData?: Record<string, any>) => void;
    };
    umamiLoaded?: boolean;
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date | Record<string, any>,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * Event properties interface for type safety
 */
export interface EventProperties {
  component?: string;
  location?: string;
  section?: string;
  action?: string;
  value?: string | number;
  url?: string;
  target?: string;
  category?: string;
  formType?: string;
  error?: string;
  success?: boolean;
  page?: string;
  feature?: string;
  plan?: string;
  [key: string]: string | number | boolean | undefined;
}

/**
 * Safe tracking function that no-ops if Umami is not available
 * @param eventName - Event name following "Feature - Action" convention
 * @param properties - Optional event properties
 */
export const track = (eventName: string, properties?: EventProperties): void => {
  try {
    // Clean properties to avoid PII leakage
    const cleanProperties = properties ? sanitizeProperties(properties) : undefined;
    
    // Check if Umami is available
    if (typeof window === 'undefined') {
      return;
    }
    
    if (!window.umami?.track) {
      // Umami not loaded yet, ignore silently
      return;
    }

    // Send to Umami
    window.umami.track(eventName, cleanProperties);
    
    // Send to Google Analytics if available
    if (window.gtag) {
      window.gtag('event', eventName.toLowerCase().replace(/\s+/g, '_'), {
        custom_parameter_1: JSON.stringify(cleanProperties || {}),
        event_category: 'user_engagement',
        ...cleanProperties
      });
    }
    
    // Log successful tracking (only in development)
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.log(`[Analytics] ${eventName}`, cleanProperties);
    }
  } catch (error) {
    // Silently fail in production, log in development
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      console.error('[Analytics] Tracking error:', error);
    }
  }
};

/**
 * Track page views manually (for SPA navigation)
 * @param path - Current path
 * @param title - Page title
 */
export const trackPageView = (path?: string, title?: string): void => {
  const currentPath = path || window.location.pathname;
  const pageName = getPageName(currentPath);
  
  track(`Page Visit - ${pageName}`, {
    page: currentPath,
    pageName: pageName,
    title: title || document.title,
    url: window.location.href,
  });
};

/**
 * Get readable page name from path
 * @param path - URL path
 * @returns Readable page name
 */
const getPageName = (path: string): string => {
  const pathMap: Record<string, string> = {
    '/': 'Startseite',
    '/uber-uns': 'Über Uns',
    '/kontakt': 'Kontakt',
    '/impressum': 'Impressum',
    '/datenschutz': 'Datenschutz'
  };
  
  return pathMap[path] || 'Unbekannte Seite';
};

/**
 * Track button clicks with standardized properties
 * @param buttonText - Button text or identifier
 * @param location - Where the button is located
 * @param component - Component name
 */
export const trackButtonClick = (
  buttonText: string,
  location: string,
  component?: string
): void => {
  const currentPage = getPageName(window.location.pathname);
  
  track(`Button Click - ${buttonText}`, {
    buttonText,
    location,
    component,
    page: currentPage,
  });
};

/**
 * Track form submissions
 * @param formType - Type of form (contact, newsletter, etc.)
 * @param success - Whether submission was successful
 * @param error - Error message if failed
 */
export const trackFormSubmission = (
  formType: string,
  success: boolean,
  error?: string
): void => {
  const currentPage = getPageName(window.location.pathname);
  const status = success ? 'Erfolgreich' : 'Fehler';
  
  track(`Formular ${formType} - ${status}`, {
    formType,
    success,
    page: currentPage,
    error: error ? 'Error occurred' : undefined, // Don't leak specific error details
  });
};

/**
 * Track navigation clicks
 * @param linkText - Link text or identifier
 * @param target - Target URL or path
 * @param location - Where the link is located (header, footer, menu)
 */
export const trackNavigation = (
  linkText: string,
  target: string,
  location: string
): void => {
  const currentPage = getPageName(window.location.pathname);
  const targetPage = getPageName(target);
  
  track(`Navigation - ${linkText}`, {
    linkText,
    target,
    targetPage,
    location,
    fromPage: currentPage,
  });
};

/**
 * Track feature interactions
 * @param feature - Feature name
 * @param action - Action performed
 * @param properties - Additional properties
 */
export const trackFeatureInteraction = (
  feature: string,
  action: string,
  properties?: EventProperties
): void => {
  const currentPage = getPageName(window.location.pathname);
  
  track(`${feature} - ${action}`, {
    feature,
    action,
    page: currentPage,
    ...properties,
  });
};

/**
 * Track modal interactions
 * @param modalName - Name of the modal
 * @param action - Action performed (open, close, submit)
 */
export const trackModal = (modalName: string, action: string): void => {
  track('Modal - ' + action, {
    component: modalName,
    action,
  });
};

/**
 * Track downloads
 * @param fileName - Name of downloaded file
 * @param fileType - Type of file
 * @param location - Where download was initiated
 */
export const trackDownload = (
  fileName: string,
  fileType: string,
  location: string
): void => {
  track('Download - File', {
    fileName: fileName.replace(/[^a-zA-Z0-9.-]/g, ''), // Sanitize filename
    fileType,
    location,
  });
};

/**
 * Track external link clicks
 * @param url - External URL
 * @param linkText - Link text
 * @param location - Where the link is located
 */
export const trackExternalLink = (
  url: string,
  linkText: string,
  location: string
): void => {
  const currentPage = getPageName(window.location.pathname);
  
  try {
    const domain = new URL(url).hostname;
    track(`External Link - ${linkText}`, {
      domain,
      linkText,
      location,
      page: currentPage,
      url: url
    });
  } catch {
    track(`External Link - ${linkText}`, {
      linkText,
      location,
      page: currentPage,
    });
  }
};

/**
 * Sanitize properties to remove potential PII
 * @param properties - Raw properties object
 * @returns Sanitized properties
 */
const sanitizeProperties = (properties: EventProperties): EventProperties => {
  const sanitized: EventProperties = {};
  
  for (const [key, value] of Object.entries(properties)) {
    if (value === undefined || value === null) continue;
    
    // Skip potentially sensitive fields
    if (key.toLowerCase().includes('email') || 
        key.toLowerCase().includes('password') ||
        key.toLowerCase().includes('phone') ||
        key.toLowerCase().includes('name')) {
      continue;
    }
    
    // Limit string length to prevent large data
    if (typeof value === 'string' && value.length > 100) {
      sanitized[key] = value.substring(0, 100) + '...';
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

/**
 * Check if analytics is available and enabled
 * @returns Boolean indicating if tracking is available
 */
export const isAnalyticsEnabled = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.umami?.track)
  );
};

/**
 * Initialize Google Analytics with GDPR consent
 */
export const initializeGoogleAnalytics = (): void => {
  try {
    if (typeof window === 'undefined') return;
    
    // Initialize dataLayer if not present
    window.dataLayer = window.dataLayer || [];
    
    // Initialize gtag function
    window.gtag = function() {
      window.dataLayer?.push(arguments);
    };
    
    // Set the current date/time
    window.gtag('js', new Date());
    
    // Configure Google Analytics with privacy settings
    window.gtag('config', 'G-RFLQ46KEG1', {
      anonymize_ip: true,
      cookie_expires: 63072000, // 2 years in seconds
      cookie_update: true,
      cookie_prefix: 'ga_',
      storage: 'cookies'
    });
    
    // Track initial page view
    trackPageView();
    
  } catch (error) {
    // Silently fail
  }
};

/**
 * Initialize analytics tracking (call this after Umami script loads)
 */
export const initializeAnalytics = (): void => {
  try {
    // Track initial page view immediately (this will handle the retry logic)
    trackPageView();
    
    // Set up automatic external link tracking
    document.addEventListener('click', (event) => {
      try {
        const target = event.target as HTMLElement;
        const link = target.closest('a[href]') as HTMLAnchorElement;
        
        if (link && link.href) {
          const url = new URL(link.href);
          const currentDomain = new URL(window.location.href).hostname;
          
          if (url.hostname !== currentDomain) {
            trackExternalLink(
              link.href,
              link.textContent?.trim() || 'External Link',
              getElementLocation(link)
            );
          }
        }
      } catch (error) {
        // Silently ignore link tracking errors
      }
    });
  } catch (error) {
    // Silently fail
  }
};

/**
 * Get element location context for tracking
 * @param element - DOM element
 * @returns Location string
 */
const getElementLocation = (element: HTMLElement): string => {
  // Check for common parent selectors
  if (element.closest('header')) return 'Header';
  if (element.closest('footer')) return 'Footer';
  if (element.closest('nav')) return 'Navigation';
  if (element.closest('[data-section]')) {
    return element.closest('[data-section]')?.getAttribute('data-section') || 'Unknown';
  }
  
  return 'Page';
};